"use client";

import { useEffect, useState } from "react";
import {
  AlertCircle,
  Calendar,
  Clock,
  ClipboardList,
  Gauge,
  Target,
} from "lucide-react";
import { calcularDesempenho, calcularTotalFazeres } from "@/lib/desempenho";

type DadosVendas = {
  totalVendas: number;
  metaMensal: number;
  ritmoDia: number;
  faltam: number;
  progressoPct: number;
};

type ClienteResumo = {
  id?: string;
  aguardandoAgendamentoDesde?: string | null;
  proximoContato?: string | null;
  concluidoHoje?: boolean;
};

type AcompanhamentoResumo = {
  clientes?: ClienteResumo[];
};

function getHojeSP() {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function dataISO(valor?: string | null) {
  if (!valor) return "";
  return new Date(valor).toISOString().slice(0, 10);
}

export function UserDesktopRail() {
  const [dadosVendas, setDadosVendas] = useState<DadosVendas | null>(null);
  const [alertasPendentes, setAlertasPendentes] = useState(0);
  const [agendarPendentes, setAgendarPendentes] = useState(0);
  const [acompanhamento, setAcompanhamento] = useState<AcompanhamentoResumo | null>(null);

  useEffect(() => {
    let ativo = true;

    const carregar = () => {
      Promise.all([
        fetch("/api/vendas", { cache: "no-store" }).then((r) => r.json()),
        fetch("/api/acompanhamento", { cache: "no-store" }).then((r) => r.json()),
        fetch("/api/clientes", { cache: "no-store" }).then((r) => r.json()),
      ])
        .then(([vendas, acompanhamentoApi, clientesApi]) => {
          if (!ativo) return;

          setDadosVendas(vendas ?? null);
          setAcompanhamento((acompanhamentoApi ?? null) as AcompanhamentoResumo | null);
          const limite = Date.now() - 3 * 24 * 60 * 60 * 1000;
          const clientes = ((clientesApi?.clientes ?? []) as ClienteResumo[]).filter((cliente) => cliente.aguardandoAgendamentoDesde);
          const alertas = clientes.filter((cliente) => new Date(cliente.aguardandoAgendamentoDesde!).getTime() < limite).length;
          setAlertasPendentes(alertas);
          setAgendarPendentes(clientes.length - alertas);
        })
        .catch(() => {});
    };

    const recarregarAoVoltar = () => {
      if (document.visibilityState === "visible") carregar();
    };

    const sincronizarConclusao = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: string; concluidoHoje?: boolean }>).detail;
      if (!detail?.id || typeof detail.concluidoHoje !== "boolean") return;

      setAcompanhamento((atual) => {
        if (!atual?.clientes?.length) return atual;
        return {
          ...atual,
          clientes: atual.clientes.map((cliente) =>
            cliente.id === detail.id ? { ...cliente, concluidoHoje: detail.concluidoHoje } : cliente,
          ),
        };
      });

    };

    carregar();
    const intervalo = window.setInterval(carregar, 5000);
    window.addEventListener("focus", carregar);
    window.addEventListener("bp:desktop-rail-refresh", carregar);
    window.addEventListener("bp:cliente-conclusao", sincronizarConclusao as EventListener);
    document.addEventListener("visibilitychange", recarregarAoVoltar);

    return () => {
      ativo = false;
      window.clearInterval(intervalo);
      window.removeEventListener("focus", carregar);
      window.removeEventListener("bp:desktop-rail-refresh", carregar);
      window.removeEventListener("bp:cliente-conclusao", sincronizarConclusao as EventListener);
      document.removeEventListener("visibilitychange", recarregarAoVoltar);
    };
  }, []);

  const hoje = getHojeSP();
  const clientesAcompanhamento = acompanhamento?.clientes ?? [];
  const paraHoje = clientesAcompanhamento.filter(
    (cliente) => !cliente.concluidoHoje && dataISO(cliente.proximoContato) === hoje,
  ).length;
  const atrasados = clientesAcompanhamento.filter(
    (cliente) => !cliente.concluidoHoje && dataISO(cliente.proximoContato) < hoje,
  ).length;
  const concluidosHoje = clientesAcompanhamento.filter((cliente) => cliente.concluidoHoje).length;
  const totaisDesempenho = {
    concluidosHoje,
    paraHoje,
    atrasados,
    paraAgendar: agendarPendentes,
    alerta: alertasPendentes,
  };
  const ritmo =
    acompanhamento == null
      ? "--"
      : calcularTotalFazeres(totaisDesempenho) === 0
        ? "--"
        : `${calcularDesempenho(totaisDesempenho)}%`;

  return (
    <aside className="hidden lg:block lg:w-[320px] lg:flex-shrink-0">
      <div className="sticky top-0 space-y-4 lg:max-h-[calc(100dvh-9rem)] lg:overflow-y-auto lg:pr-1">
        <section className="rounded-[16px] border border-[var(--border)] bg-card p-4">
          <div className="flex items-start gap-2">
            <Target className="mt-0.5 h-4 w-4 text-[var(--text-primary)]" />
            <div>
              <p className="text-[14px] font-semibold text-[var(--text-primary)]">Meta do mes</p>
              <p className="text-[11px] text-[var(--text-secondary)]">Acompanhe suas vendas e evolucao.</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <DesktopSidebarMetric label="Vendas atuais" valor={dadosVendas?.totalVendas ?? 0} />
            <DesktopSidebarMetric label="Meta do mês" valor={dadosVendas?.metaMensal ?? 0} />
            <DesktopSidebarMetric label="Faltam apenas" valor={dadosVendas?.faltam ?? 0} />
          </div>
        </section>

        <section className="rounded-[16px] border border-[var(--border)] bg-card p-4">
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold text-[var(--text-primary)]">Progresso do mes</p>
            <span className="text-[11px] text-[var(--text-secondary)]">{dadosVendas?.progressoPct ?? 0}% da meta</span>
          </div>

          <div className="mt-3 h-[7px] rounded-full bg-[var(--border-subtle)]">
            <div className="h-[7px] rounded-full bg-[#596A3A]" style={{ width: `${dadosVendas?.progressoPct ?? 0}%` }} />
          </div>

          <p className="mt-3 text-[11px] text-[var(--text-secondary)]">Mantenha o ritmo para evoluir no dia.</p>
        </section>

        <section className="rounded-[16px] border border-[var(--border)] bg-card p-4">
          <div className="mb-4 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[var(--text-primary)]" />
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">Pulso do dia</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <DesktopResumoCard
              label="Hoje"
              valor={paraHoje}
              Icon={ClipboardList}
              cor="var(--dashboard-pulse-hoje-color)"
              fundo="var(--dashboard-pulse-hoje-bg)"
            />
            <DesktopResumoCard
              label="Atrasados"
              valor={atrasados}
              Icon={Clock}
              cor="#D98B3E"
              fundo="rgba(217,139,62,0.12)"
            />
            <DesktopResumoCard label="Agendar" valor={agendarPendentes} Icon={Calendar} cor="var(--dashboard-pulse-hoje-color)" fundo="var(--dashboard-pulse-hoje-bg)" />
            <DesktopResumoCard
              label="Alerta"
              valor={alertasPendentes}
              Icon={AlertCircle}
              cor="var(--dashboard-alert-text)"
              fundo="var(--dashboard-alert-bg)"
            />
            <DesktopResumoCard label="Ritmo" valor={ritmo} Icon={Gauge} cor="#3E6CFE" fundo="rgba(62,108,254,0.12)" />
          </div>
        </section>

        <section className="rounded-[16px] border border-[var(--border)] bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-[var(--text-primary)]" />
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">Alertas</p>
            </div>
            <span className="text-[28px] font-semibold leading-none text-[#D98B3E]">{alertasPendentes}</span>
          </div>
          <p className="mt-3 text-[12px] font-medium text-[var(--text-primary)]">Atencoes pendentes</p>
          <p className="mt-1 text-[11px] text-[var(--text-secondary)]">Itens precisam da sua atencao.</p>
        </section>
      </div>
    </aside>
  );
}

function DesktopSidebarMetric({ label, valor, subtitulo }: { label: string; valor: number; subtitulo?: string }) {
  return (
    <div className="flex min-h-[116px] flex-col items-center justify-center rounded-[18px] border border-[var(--border)] bg-card px-4 py-5 text-center shadow-[var(--shadow-soft)]">
      <p className="text-[11px] uppercase tracking-wide text-[var(--text-soft)]">{label}</p>
      <p className="mt-3 text-[34px] font-semibold leading-none text-[var(--text-primary)]">{valor}</p>
      {subtitulo ? <p className="mt-2 text-[12px] text-[var(--text-soft)]">{subtitulo}</p> : null}
    </div>
  );
}

function DesktopResumoCard({
  label,
  valor,
  Icon,
  cor,
  fundo,
}: {
  label: string;
  valor: number | string;
  Icon: React.ElementType;
  cor: string;
  fundo: string;
}) {
  return (
    <div className="flex min-h-[104px] flex-col items-center justify-center gap-3 rounded-[16px] border border-[var(--border)] bg-card px-4 py-5 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-[14px]" style={{ background: fundo, color: cor }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="space-y-1">
        <span className="block text-[13px] font-semibold text-[var(--text-primary)]">{label}</span>
        <span className="block text-[22px] font-semibold leading-none text-[var(--text-primary)]">{valor}</span>
      </div>
    </div>
  );
}
